/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { z } from 'zod'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'

const schema = z.object({
  enabled: z.boolean(),
  maxSpinsPerDay: z.coerce.number().int().min(1),
  costPerSpin: z.coerce.number().int().min(1),
  minMatch2Reward: z.coerce.number().int().min(0),
  maxMatch2Reward: z.coerce.number().int().min(0),
  minMatch3Reward: z.coerce.number().int().min(0),
  maxMatch3Reward: z.coerce.number().int().min(0),
  minBombPenalty: z.coerce.number().int().min(1),
  maxBombPenalty: z.coerce.number().int().min(1),
})

type Values = z.infer<typeof schema>

export function SlotMachineSettingsSection({
  defaultValues,
}: {
  defaultValues: Values
}) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()

  const form = useForm<Values>({
    resolver: zodResolver(schema) as unknown as Resolver<Values>,
    defaultValues,
  })

  const { isDirty, isSubmitting } = form.formState
  const enabled = form.watch('enabled')

  async function onSubmit(values: Values) {
    const updates: Array<{ key: string; value: string }> = []

    if (values.enabled !== defaultValues.enabled) {
      updates.push({
        key: 'slot_machine_setting.enabled',
        value: String(values.enabled),
      })
    }

    if (values.maxSpinsPerDay !== defaultValues.maxSpinsPerDay) {
      updates.push({
        key: 'slot_machine_setting.max_spins_per_day',
        value: String(values.maxSpinsPerDay),
      })
    }

    if (values.costPerSpin !== defaultValues.costPerSpin) {
      updates.push({
        key: 'slot_machine_setting.cost_per_spin',
        value: String(values.costPerSpin),
      })
    }

    if (values.minMatch2Reward !== defaultValues.minMatch2Reward) {
      updates.push({
        key: 'slot_machine_setting.min_match2_reward',
        value: String(values.minMatch2Reward),
      })
    }

    if (values.maxMatch2Reward !== defaultValues.maxMatch2Reward) {
      updates.push({
        key: 'slot_machine_setting.max_match2_reward',
        value: String(values.maxMatch2Reward),
      })
    }

    if (values.minMatch3Reward !== defaultValues.minMatch3Reward) {
      updates.push({
        key: 'slot_machine_setting.min_match3_reward',
        value: String(values.minMatch3Reward),
      })
    }

    if (values.maxMatch3Reward !== defaultValues.maxMatch3Reward) {
      updates.push({
        key: 'slot_machine_setting.max_match3_reward',
        value: String(values.maxMatch3Reward),
      })
    }

    if (values.minBombPenalty !== defaultValues.minBombPenalty) {
      updates.push({
        key: 'slot_machine_setting.min_bomb_penalty',
        value: String(values.minBombPenalty),
      })
    }

    if (values.maxBombPenalty !== defaultValues.maxBombPenalty) {
      updates.push({
        key: 'slot_machine_setting.max_bomb_penalty',
        value: String(values.maxBombPenalty),
      })
    }

    if (updates.length === 0) {
      toast.info(t('No changes to save'))
      return
    }

    for (const update of updates) {
      await updateOption.mutateAsync(update)
    }

    form.reset(values)
  }

  return (
    <SettingsSection
      title={t('Slot Machine Rewards')}
      description={t('Configure slot machine rewards and limits')}
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          autoComplete='off'
          className='space-y-6'
        >
          <FormField
            control={form.control}
            name='enabled'
            render={({ field }) => (
              <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                <div className='space-y-0.5'>
                  <FormLabel className='text-base'>
                    {t('Enable slot machine')}
                  </FormLabel>
                  <FormDescription>
                    {t('Try your luck and win quota rewards')}
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={updateOption.isPending || isSubmitting}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {enabled && (
            <>
              <div className='grid gap-6 sm:grid-cols-3'>
                <FormField
                  control={form.control}
                  name='maxSpinsPerDay'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Max spins per day')}</FormLabel>
                      <FormControl>
                        <Input type='number' min={1} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='costPerSpin'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Cost per spin')}</FormLabel>
                      <FormControl>
                        <Input type='number' min={1} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid gap-6 sm:grid-cols-2'>
                <FormField
                  control={form.control}
                  name='minMatch2Reward'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Minimum reward for 2 matching symbols')}</FormLabel>
                      <FormControl>
                        <Input type='number' min={0} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='maxMatch2Reward'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Maximum reward for 2 matching symbols')}</FormLabel>
                      <FormControl>
                        <Input type='number' min={0} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid gap-6 sm:grid-cols-2'>
                <FormField
                  control={form.control}
                  name='minMatch3Reward'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Minimum reward for 3 matching symbols')}</FormLabel>
                      <FormControl>
                        <Input type='number' min={0} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='maxMatch3Reward'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Maximum reward for 3 matching symbols')}</FormLabel>
                      <FormControl>
                        <Input type='number' min={0} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid gap-6 sm:grid-cols-2'>
                <FormField
                  control={form.control}
                  name='minBombPenalty'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Minimum bomb penalty')}</FormLabel>
                      <FormControl>
                        <Input type='number' min={1} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='maxBombPenalty'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('Maximum bomb penalty')}</FormLabel>
                      <FormControl>
                        <Input type='number' min={1} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}

          <Button
            type='submit'
            disabled={!isDirty || updateOption.isPending || isSubmitting}
          >
            {updateOption.isPending || isSubmitting
              ? t('Saving...')
              : t('Save slot machine settings')}
          </Button>
        </form>
      </Form>
    </SettingsSection>
  )
}
